import React, { useRef, useEffect, useState } from 'react'
import { motion } from 'framer-motion'
// Removed react-icons import - using inline SVG instead
import { usePublicContent } from '../hooks/usePublicContent'
import { HeroContent } from '../types/content'
import TrustStrip from './TrustStrip'

const Hero: React.FC = () => {
  const { content, loading } = usePublicContent('hero')
  const heroData = content?.data as HeroContent
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(true)

  // Video control functions
  const handlePlayPause = async () => {
    const video = videoRef.current
    if (!video) return

    if (video.paused) {
      try {
        await video.play()
        setIsPlaying(true)
      } catch (error) {
        console.error('Failed to play video:', error)
      }
    } else {
      video.pause()
      setIsPlaying(false)
    }
  }

  const handleMuteToggle = () => {
    const video = videoRef.current
    if (!video) return
    video.muted = !video.muted
    setIsMuted(video.muted)
  }

  // Sync state with video element
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const syncState = () => {
      setIsPlaying(!video.paused)
      setIsMuted(video.muted)
    }

    video.addEventListener('play', syncState)
    video.addEventListener('pause', syncState)
    video.addEventListener('volumechange', syncState)

    return () => {
      video.removeEventListener('play', syncState)
      video.removeEventListener('pause', syncState)
      video.removeEventListener('volumechange', syncState)
    }
  }, [])

  if (loading) {
    return (
      <section className="min-h-screen flex items-center justify-center bg-neutral-900">
        <div className="text-center text-white">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p>Loading...</p>
        </div>
      </section>
    )
  }

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 sm:pt-0">
      {/* Video Background */}
      <div className="absolute inset-0 video-container">
        <video
          ref={videoRef}
          src={heroData?.videoUrl}
          className="w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          style={{ backgroundColor: '#171717' }}
        />
        
        {/* Video Overlay - below video controls but above video */}
        <div className="absolute inset-0 bg-black/60 pointer-events-none"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-primary/20 via-transparent to-secondary/30 pointer-events-none"></div>
        
        {/* Video Controls - Bottom Right - Combined Vertical Pill */}
        <div className="absolute bottom-20 right-6 z-20">
          <div className="flex flex-col bg-black bg-opacity-70 backdrop-blur-sm rounded-full p-2 shadow-lg">
            {/* Play/Pause Button */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={handlePlayPause}
              className="w-10 h-10 flex items-center justify-center text-white hover:bg-white/10 rounded-full transition-all duration-200 cursor-pointer"
              title={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? (
                <svg 
                  width="18" 
                  height="18" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="white" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  style={{ display: 'block', flexShrink: 0 }}
                >
                  <rect x="6" y="4" width="4" height="16"></rect>
                  <rect x="14" y="4" width="4" height="16"></rect>
                </svg>
              ) : (
                <svg 
                  width="18" 
                  height="18" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="white" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  className="ml-0.5"
                  style={{ display: 'block', flexShrink: 0 }}
                >
                  <polygon points="5,3 19,12 5,21"></polygon>
                </svg>
              )}
            </motion.button>

            {/* Divider */}
            <div className="h-px bg-white/20 my-1"></div>

            {/* Mute/Unmute Button */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleMuteToggle}
              className="w-10 h-10 flex items-center justify-center text-white hover:bg-white/10 rounded-full transition-all duration-200 cursor-pointer"
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? (
                <svg 
                  width="18" 
                  height="18" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="white" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  style={{ display: 'block', flexShrink: 0 }}
                >
                  <polygon points="11,5 6,9 2,9 2,15 6,15 11,19"></polygon>
                  <line x1="23" y1="9" x2="17" y2="15"></line>
                  <line x1="17" y1="9" x2="23" y2="15"></line>
                </svg>
              ) : (
                <svg 
                  width="18" 
                  height="18" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="white" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  style={{ display: 'block', flexShrink: 0 }}
                >
                  <polygon points="11,5 6,9 2,9 2,15 6,15 11,19"></polygon>
                  <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                </svg>
              )}
            </motion.button>
          </div>
        </div>

        {/* Trust Strip - venue logos overlaying bottom of video */}
        <TrustStrip />
      </div>

      {/* Content */}
      <div className="relative z-20 text-center px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto hero-content">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="flex flex-col items-center justify-center min-h-screen"
        >
          {/* O-Live Music Logo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-8 relative z-20"
          >
            <img
              src="/assets/LOGO - TRANSPARENT WHITE.png"
              alt="O-Live Music"
              className="h-32 sm:h-40 md:h-48 lg:h-56 xl:h-64 w-auto mx-auto"
            />
          </motion.div>

          {/* Tagline */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-white text-lg sm:text-xl md:text-2xl font-light mb-10 tracking-wide relative z-30 -mt-12"
          >
            {/* Live Music Perfectly Curated */}
          </motion.h2>

          {/* Call to Action Text */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative z-40 -mt-12 text-center"
            style={{ marginTop: '-75px' }}
          >
            <a
              href="#contact"
              className="group relative inline-block text-white/60 hover:text-white transition-all duration-500 cursor-pointer"
              onClick={(e) => {
                e.preventDefault()
                document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })
              }}
            >
              <span className="text-lg sm:text-xl font-extralight tracking-wide relative overflow-hidden">
                <span className="relative z-10">
                  {heroData?.clickableText || 'Bespoke Curation for Live Events'}
                </span>
                {/* Gradient overlay that animates over the text */}
                <span className="absolute inset-0 bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 ease-out transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 ease-out"></span>
              </span>
            </a>
            
            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="mt-6"
            >
              <a
                href="#contact"
                className="inline-block px-8 py-3 rounded-full transition-all duration-300 cursor-pointer text-white font-light text-sm tracking-wide"
                style={{ 
                  backgroundColor: 'rgba(97, 131, 158, 0.5)', // Brand blue with 50% opacity
                  border: '1px solid rgba(97, 131, 158, 0.3)',
                  backdropFilter: 'blur(10px)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(97, 131, 158, 0.7)'
                  e.currentTarget.style.borderColor = 'rgba(97, 131, 158, 0.5)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(97, 131, 158, 0.5)'
                  e.currentTarget.style.borderColor = 'rgba(97, 131, 158, 0.3)'
                }}
                onClick={(e) => {
                  e.preventDefault()
                  document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })
                }}
              >
                {heroData?.ctaButtonText || 'Get in Touch'}
              </a>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}

export default Hero
